<template>
  <client-only>
    <div 
      class="ui-setting-menu relative" 
      :class="{ 'active': selected, 'hide': !visible }" 
      @click="$emit('click', $event)" 
      @mouseenter="onMouseEnter" 
      @mouseleave="onMouseLeave"
    >
      <slot>
        <div class="flex items-center flex-1 min-w-0">
          <im-icon v-if="icon" :icon="icon" class="item-icon mr-2.5 text-16" />
          <span class="name flex-1 truncate text-13 font-medium">
            {{ label }}
          </span>
        </div>
        
        <div class="handle-box">
          <div v-if="deleteVisible || eyeVisible || dragVisible || isLeaf" class="handle-wrapper">
            <!-- Delete Button -->
            <div 
              v-if="deleteVisible" 
              class="action-btn delete" 
              @click.stop="handleRemove"
              title="Delete"
            >
              <i class="el-icon-delete icon-svg"></i>
            </div>

            <!-- Visibility Toggle -->
            <div 
              v-if="eyeVisible" 
              class="action-btn visibility" 
              @click.stop="handleToggleVisible"
              :title="visible ? 'Hide' : 'Show'"
            >
              <i 
                class="icon-svg" 
                :class="visible ? 'el-icon-view' : 'el-icon-lock'"
                :style="{ opacity: visible ? 1 : 0.5 }"
              ></i>
            </div>

            <!-- Drag Handle -->
            <div 
              v-if="dragVisible" 
              class="action-btn drag move-btn"
              title="Drag to reorder"
            >
              <i class="el-icon-rank icon-svg"></i>
            </div>

            <!-- Expand/Collapse -->
            <div 
              v-if="isLeaf" 
              class="action-btn expand-trigger" 
              @click.stop="handleLeafClick"
            >
              <i 
                class="el-icon-caret-right arrow-icon" 
                :class="{ 'is-expanded': expand }" 
              ></i>
            </div>
          </div>
        </div>
      </slot>
    </div>
  </client-only>
</template>

<script setup>
import { ref, computed } from 'vue'
import ImIcon from "~/components/common/ImIcon"

const props = defineProps({
  global: {
    type: Boolean,
    default: false,
  },
  index: {
    type: String,
    default: '',
  },
  isTutorial: {
    type: Boolean,
    default: false
  },
  toggleDisplay: {
    type: Boolean,
    default: false
  },
  info: {
    type: Object,
    default: () => Object.freeze({})
  },
  isDefault: {
    type: Boolean,
    default: false
  },
  visible: {
    type: Boolean,
    default: true
  },
  dragging: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: ''
  },
  selected: {
    type: Boolean,
    default: false
  },
  draggable: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  },
  isLeaf: {
    type: Boolean,
    default: false
  },
  expand: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['click', 'leaf-click', 'visible-change', 'delete'])

const show = ref(false)
const dragVisible = computed(() => {
  const base = (show.value && props.draggable) && props.visible
  return base || props.isTutorial
})

const eyeVisible = computed(() => {
  const base = (!props.global && !props.isDefault) || props.isTutorial
  return base || props.toggleDisplay
})

const deleteVisible = computed(() => {
  const base = (!props.global && !props.isDefault) && show.value
  return (base && props.visible) || props.isTutorial
})


const handleLeafClick = () => {
  emit('leaf-click')
}

const onMouseEnter = (event) => {
  event.preventDefault()
  if (props.dragging) {
    return
  }
  show.value = true
}

const handleToggleVisible = () => {
  emit('visible-change')
}

const handleRemove = () => {
  emit('delete')
}

const onMouseLeave = (event) => {
  event.preventDefault()
  show.value = false
}
</script>

<style lang="less" scoped>
.ui-setting-menu {
  display: flex;
  height: 38px;
  line-height: 38px;
  padding-left: 12px;
  position: relative;
  background: transparent;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s cubic-bezier(0.2, 0, 0, 1);
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;
  // color: #e5e7eb;
  margin-bottom: 4px;
  border: 1px solid transparent;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 12px;
    right: 12px;
    height: 1px;
    background: rgba(255, 255, 255, 0.03);
    transition: opacity 0.2s;
  }

  .toggle-visible {
    display: none;
  }

  .item-icon {
    color: rgba(255, 255, 255, 0.4);
    transition: all 0.2s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    color: rgba(255, 255, 255, 0.9);
    &::after {
      opacity: 0;
    }
    
    .item-icon {
      color: rgba(255, 255, 255, 0.9);
      transform: scale(1.1);
    }

    .toggle-visible {
      display: block;
    }
  }

  &.active {
    background: rgba(var(--primary-color-rgb), 0.1);
    border-color: rgba(var(--primary-color-rgb), 0.2);
    color: #fff;
    font-weight: 500;

    &::after {
      opacity: 0;
    }

    .item-icon {
      color: var(--primary-color);
    }

    .leaf-icon {
      fill: var(--primary-color);
    }
    .handle-box {
      .im-icon {
        color: var(--primary-color);
      }
    }
  }

  &.hide {
    opacity: 0.5;
    
    .leaf-icon {
      fill: rgba(255, 255, 255, 0.2);
    }
    .toggle-visible {
      color: rgba(255, 255, 255, 0.4);
    }
    .name {
      text-decoration: line-through;
    }
  }

  .leaf-icon {
    display: block;
    width: 12px;
    height: 12px;
    fill: rgba(255, 255, 255, 0.4);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &.expand {
      transform: rotate(90deg);
      fill: #fff;
    }
  }

  .handle-box {
    position: absolute;
    right: 8px;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;

    .handle-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px;
      background: rgba(18, 18, 23, 0.6);
      border-radius: 8px;
      backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.05);
      
      .action-btn {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
        color: rgba(255, 255, 255, 0.5);
        background: transparent;

        .icon-svg {
          fill: currentColor;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
          transform: scale(1.05);
        }

        &.delete:hover {
          background: rgba(245, 108, 108, 0.15);
          color: #f56c6c;
        }

        &.visibility:hover {
          background: rgba(var(--primary-color-rgb), 0.15);
          color: var(--primary-color);
        }
        
        &.expand-trigger {
          .arrow-icon {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.4);
            transition: transform 0.3s ease;
            transform: rotate(0deg);
            
            &.is-expanded {
              transform: rotate(90deg);
              color: #fff;
            }
          }
        }
      }
    }
  }
}
</style>